import { groupBy } from 'es-toolkit'
import { useCallback } from 'react'
import { useDeviceRecord } from '../../data/devices.ts'
import type { ExpandedMeasurement } from '../../data/liveMeasurements.ts'
import type { MeasurementType } from '../../data/measurementTypes.ts'
import { dayjs } from '../../dayjs.ts'
import { isMeasurementTypeBlacklisted } from './measurementBlacklist.ts'
import { getSensorPriority } from './sensorPriority.ts'

export const useMeasurementUtils = () => {
	const { data: deviceRecord } = useDeviceRecord()

	const getPrimaryMeasurement = useCallback(
		<T extends { sourceId: string; measurementType: MeasurementType }>(
			measurements: T[] | undefined,
			blacklist?: Parameters<typeof isMeasurementTypeBlacklisted>[1],
		) => _getPrimaryMeasurement(measurements, deviceRecord, blacklist),
		[deviceRecord],
	)

	return { getPrimaryMeasurement, getLowestQuality }
}

const getLowestQuality = (measurements: ExpandedMeasurement[] | undefined) => {
	return measurements?.reduce((lowestQuality, measurement) => {
		if (!lowestQuality) {
			return measurement.quality
		}
		return measurement.quality.qualityValue < lowestQuality.qualityValue ? measurement.quality : lowestQuality
	}, measurements.at(0)?.quality)
}

const _getPrimaryMeasurement = <T extends { sourceId: string; measurementType: MeasurementType }>(
	rawMeasurements: T[] | undefined,
	deviceIdRecord: ReturnType<typeof useDeviceRecord>['data'],
	blacklist?: Parameters<typeof isMeasurementTypeBlacklisted>[1],
) => {
	if (!rawMeasurements || rawMeasurements.length === 0) {
		return
	}

	// Filter out blacklisted measurements that we don't want to show
	const measurements = rawMeasurements.filter((measurement) => !isMeasurementTypeBlacklisted(measurement.measurementType, blacklist))

	if (measurements.length === 0) {
		return
	}

	// We only got one measurement, return it
	if (measurements.length === 1) {
		return measurements
	}

	// We want to prefer VALUE over MEAN and choose 10 minute period if possible, otherwise the shortest
	const statisticGroups = groupBy(measurements, (measurement) => measurement.measurementType.statistic)
	const goodStatistic = statisticGroups.VALUE || Object.values(statisticGroups).flat()

	// Remove MIN and MAX. Looks kinda of confusing in here, but due to the usage of the function, they
	// only get removed from aggregated data. The table will have them separated before so they get shown
	// in there due to the measurements.length === 1 check. But from graph data they get removed by this check.
	// The wanted parameters for min and max are wind related and they get handled as their own "measurement type" elsewhere.
	const nonMinMax = goodStatistic.filter(
		(measurement) => measurement.measurementType.statistic !== 'MINIMUM' && measurement.measurementType.statistic !== 'MAXIMUM',
	)
	const selectedStatistics = nonMinMax.length > 0 ? nonMinMax : goodStatistic

	const periodGroups = groupBy(selectedStatistics, (measurement) => measurement.measurementType.period)
	const sortedPeriods = Object.keys(periodGroups).sort((a, b) => {
		return dayjs.duration(a).asSeconds() - dayjs.duration(b).asSeconds()
	})

	const smallestPeriod = sortedPeriods[0]
	const largestPeriod = sortedPeriods.at(-1)
	if (smallestPeriod == null || largestPeriod == null) return
	// Prioritize largest period for SUM statistics, as that produces the daily accumulation. Otherwise prefer 10 min or the lowest.
	const isSumStatisticSelected = selectedStatistics.some((measurement) => measurement.measurementType.statistic === 'SUM')
	const bestMeasurements = isSumStatisticSelected ? periodGroups[largestPeriod] : periodGroups.PT10M || periodGroups[smallestPeriod]

	// All measurements are from same type of sensors, return all measurements as an array
	const sensorProductCodes = bestMeasurements?.map((measurement) => {
		return deviceIdRecord?.[measurement.sourceId]?.productCode
	})
	if (new Set(sensorProductCodes).size < (bestMeasurements?.length ?? 0)) {
		return bestMeasurements
	}

	// Choose one based on the product codes
	const sorted = bestMeasurements?.sort((a, b) => {
		const deviceA = deviceIdRecord?.[a.sourceId]
		const deviceB = deviceIdRecord?.[b.sourceId]
		const aIndex = getSensorPriority(deviceA?.productCode || '')
		const bIndex = getSensorPriority(deviceB?.productCode || '')
		return aIndex - bIndex
	})
	return [sorted?.[0]] as T[]
}
